'use client';
import { AlertTriangle, RefreshCw, Ruler } from 'lucide-react';

import { useState, useCallback } from 'react';
import { useTheme } from '@/components/ThemeContext';
import { getThemeStyles } from '@/lib/themeStyles';

/* ═══════════════════════════════════════════════
   BOQ Automation Dashboard
   DXF 파싱 → Notion 수량산출 동기화 파이프라인 UI
   ═══════════════════════════════════════════════ */

// Lucide-style SVG helpers
const I = ({ d, c = 'currentColor', s = 18 }: { d: string; c?: string; s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

const ico = {
  upload:    'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  play:      'M5 3l14 9-14 9V3z',
  eye:       'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 100 6 3 3 0 000-6z',
  check:     'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
  plus:      'M12 5v14M5 12h14',
  edit:      'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  trash:     'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6',
  equal:     'M5 9h14M5 15h14',
  alert:     'M12 2a10 10 0 100 20 10 10 0 000-20zM12 8v4M12 16h.01',
  file:      'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  folder:    'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  layers:    'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  bar:       'M12 20V10M18 20V4M6 20v-4',
  refresh:   'M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 005.64 5.64L1 10M22.99 14.01A9 9 0 0118.36 18.36L23 14',
  home:      'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z',
  arrowLeft: 'M19 12H5M12 19l-7-7 7-7',
  zap:       'M13 2L3 14h9l-1 10 10-12h-9l1-10z',
  database:  'M12 2a9 4 0 100 8 9 4 0 000-8zM3 6v6c0 2.21 4.03 4 9 4s9-1.79 9-4V6M3 12v6c0 2.21 4.03 4 9 4s9-1.79 9-4v-6',
  ruler:     'M2 2l20 20M5.64 5.64l3.54 3.54M9.88 1.76l1.41 1.41M17.66 9.88l1.41 1.41M1.76 9.88l1.41 1.41M14.12 14.12l3.54 3.54',
  grid:      'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  link:      'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
};

type Stage = 'idle' | 'uploading' | 'parsing' | 'diffing' | 'syncing' | 'done' | 'error';
type Tab = 'pipeline' | 'results' | 'chain';

interface ParseSummary {
  layerCount: number; finishLayerCount: number;
  polylineCount: number; hatchCount: number;
  textCount: number; insertCount: number;
  roomTagCount: number;
}

interface Quantity {
  roomTag: string; specCode: string; category: string;
  quantity: number; unit: string; basis: string; layerName: string;
}

interface DiffItem {
  action: 'create' | 'update' | 'unchanged' | 'removed';
  roomTag: string; specCode: string;
  oldQty?: number; newQty?: number;
  delta?: number; deltaPercent?: string;
}

export default function BoqAutomationPage() {
  const [tab, setTab] = useState<Tab>('pipeline');
  const [stage, setStage] = useState<Stage>('idle');
  const [dxfText, setDxfText] = useState('');
  const [nasPath, setNasPath] = useState('');
  const [wallHeight, setWallHeight] = useState('2.7');
  const [isDryRun, setIsDryRun] = useState(true);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // DXF 파일 업로드 핸들러
  const handleFileUpload = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.dxf')) {
      setError('DXF 파일만 지원됩니다.');
      return;
    }
    setStage('uploading');
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      setDxfText(e.target?.result as string || '');
      setNasPath(file.name);
      setStage('idle');
    };
    reader.onerror = () => { setError('파일 읽기 실패'); setStage('error'); };
    reader.readAsText(file);
  }, []);

  // 파이프라인 실행
  const runPipeline = useCallback(async () => {
    setError('');
    setResult(null);
    setStage('parsing');

    try {
      const body: any = {
        wallHeight: parseFloat(wallHeight) || 2.7,
        dryRun: isDryRun,
      };

      if (dxfText) {
        body.dxfText = dxfText;
        body.filePath = nasPath || 'uploaded.dxf';
      } else if (nasPath) {
        body.filePath = nasPath;
      } else {
        setError('DXF 파일을 업로드하거나 NAS 경로를 입력하세요.');
        setStage('error');
        return;
      }

      setStage('diffing');

      const res = await fetch('/api/boq-automation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      setStage(isDryRun ? 'done' : 'syncing');
      setResult(data);
      setStage('done');

    } catch (e: any) {
      setError(e.message || 'Pipeline failed');
      setStage('error');
    }
  }, [dxfText, nasPath, wallHeight, isDryRun]);

  const diffCounts = result?.syncResult?.diff ? {
    create: result.syncResult.diff.filter((d: DiffItem) => d.action === 'create').length,
    update: result.syncResult.diff.filter((d: DiffItem) => d.action === 'update').length,
    unchanged: result.syncResult.diff.filter((d: DiffItem) => d.action === 'unchanged').length,
    removed: result.syncResult.diff.filter((d: DiffItem) => d.action === 'removed').length,
  } : null;

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const t = getThemeStyles(isDark);

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: 64,
      background: t.pageBg3,
      color: t.textLight,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      {/* ── Header ── */}
      <header style={{
        padding: '16px 28px',
        borderBottom: `1px solid ${t.borderLight}`,
        background: t.headerBg,
        backdropFilter: 'blur(24px)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, maxWidth: 1280, margin: '0 auto' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 11,
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(16,185,129,0.3)',
          }}>
            <I d={ico.ruler} c="#fff" s={20} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', background: t.heroGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              BOQ Automation
            </h1>
            <p style={{ margin: 0, fontSize: 11, color: t.textMuted }}>
              DXF → Notion 수량산출 자동 동기화
            </p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <a href="/boq-agent" style={{ color: '#60a5fa', textDecoration: 'none', fontSize: 11, padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(96,165,250,0.2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <I d={ico.bar} c="#60a5fa" s={13} /> BOQ Agent
            </a>
            <a href="/ptk" style={{ color: '#a78bfa', textDecoration: 'none', fontSize: 11, padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(167,139,250,0.2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <I d={ico.layers} c="#a78bfa" s={13} /> PTK
            </a>
            <a href="/" style={{ color: '#64748b', textDecoration: 'none', fontSize: 11, padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(100,116,139,0.15)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <I d={ico.home} c="#64748b" s={13} /> Home
            </a>
          </div>
        </div>
      </header>

      {/* ── Tab Nav ── */}
      <nav style={{ maxWidth: 1280, margin: '0 auto', padding: '0 28px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {([
            { key: 'pipeline', label: '파이프라인', icon: ico.zap },
            { key: 'results', label: '산출 결과', icon: ico.bar },
            { key: 'chain', label: 'CostContext 체인', icon: ico.link },
          ] as { key: Tab; label: string; icon: string }[]).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '12px 18px', fontSize: 12, fontWeight: 600,
              color: tab === t.key ? '#e2e8f0' : '#475569',
              background: 'transparent', border: 'none',
              borderBottom: `2px solid ${tab === t.key ? '#10b981' : 'transparent'}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.2s',
            }}>
              <I d={t.icon} c={tab === t.key ? '#10b981' : '#475569'} s={14} />
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 28px' }}>

        {/* ════ Pipeline Tab ════ */}
        {tab === 'pipeline' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Left: Input */}
            <div>
              {/* DXF Upload Zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0]); }}
                style={{
                  border: `2px dashed ${dragOver ? '#10b981' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: 16, padding: '32px 24px', textAlign: 'center',
                  background: dragOver ? 'rgba(16,185,129,0.05)' : 'rgba(255,255,255,0.015)',
                  transition: 'all 0.3s', marginBottom: 16, cursor: 'pointer',
                }}
                onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = '.dxf'; input.onchange = (e: any) => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }; input.click(); }}
              >
                <I d={ico.upload} c={dragOver ? '#10b981' : '#64748b'} s={32} />
                <p style={{ margin: '12px 0 4px', fontSize: 14, fontWeight: 600, color: dragOver ? '#10b981' : '#94a3b8' }}>
                  DXF 파일 드래그 또는 클릭
                </p>
                <p style={{ margin: 0, fontSize: 11, color: '#475569' }}>
                  AutoCAD DXF 파일 (.dxf) — 레이어 규칙 FIN-FLR-*, FIN-WAL-* 등 적용
                </p>
                {nasPath && dxfText && (
                  <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <I d={ico.check} c="#10b981" s={14} />
                    <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>{nasPath}</span>
                    <span style={{ fontSize: 10, color: '#64748b' }}>({(dxfText.length / 1024).toFixed(0)} KB)</span>
                  </div>
                )}
              </div>

              {/* NAS Path Input */}
              <div style={{
                background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.06)', padding: '18px 20px', marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <I d={ico.folder} c="#f59e0b" s={15} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>NAS 경로 (직접 입력)</span>
                </div>
                <input
                  value={nasPath} onChange={e => setNasPath(e.target.value)}
                  placeholder="B_DESIGN/[2025_PROJECT]/ID_2506_A프로젝트/03_DWG/도면.dxf"
                  style={{
                    width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8, padding: '9px 14px', color: '#e2e8f0', fontSize: 12, outline: 'none',
                    transition: 'border-color 0.2s', boxSizing: 'border-box',
                  }}
                  onFocus={e => (e.target.style.borderColor = 'rgba(16,185,129,0.4)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.08)')}
                />
              </div>

              {/* Options */}
              <div style={{
                background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.06)', padding: '18px 20px', marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <I d={ico.grid} c="#818cf8" s={15} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>설정</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 10, color: '#64748b', display: 'block', marginBottom: 4 }}>벽체 높이 (m)</label>
                    <input type="number" step="0.1" value={wallHeight} onChange={e => setWallHeight(e.target.value)}
                      style={{ width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: '#64748b', display: 'block', marginBottom: 4 }}>실행 모드</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: isDryRun ? '#f59e0b' : '#10b981', cursor: 'pointer', padding: '7px 12px', borderRadius: 8, background: isDryRun ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${isDryRun ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)'}` }}>
                      <input type="checkbox" checked={isDryRun} onChange={e => setIsDryRun(e.target.checked)}
                        style={{ accentColor: '#f59e0b', width: 14, height: 14, cursor: 'pointer' }} />
 {isDryRun ? ' 미리보기 (Dry Run)' : ' 실제 동기화'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Run Button */}
              <button onClick={runPipeline}
                disabled={stage === 'parsing' || stage === 'diffing' || stage === 'syncing'}
                style={{
                  width: '100%', padding: '14px 22px',
                  background: (stage === 'parsing' || stage === 'diffing' || stage === 'syncing')
                    ? 'rgba(16,185,129,0.3)'
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none', borderRadius: 12, color: '#fff', fontWeight: 700,
                  cursor: (stage === 'parsing' || stage === 'diffing') ? 'wait' : 'pointer',
                  fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: '0 0 30px rgba(16,185,129,0.2)',
                  opacity: (stage === 'parsing' || stage === 'diffing' || stage === 'syncing') ? 0.6 : 1,
                  transition: 'all 0.3s',
                }}>
                <I d={stage === 'done' ? ico.check : ico.play} c="#fff" s={18} />
 {stage === 'parsing' ? ' DXF 파싱 중...'
 : stage === 'diffing' ? ' Diff 비교 중...'
 : stage === 'syncing' ? ' Notion 동기화 중...'
 : stage === 'done' ? ' 완료'
 : isDryRun ? ' 미리보기 실행' : ' 파이프라인 실행'}
              </button>
            </div>

            {/* Right: Results */}
            <div>
              {/* Stage Indicator */}
              <div style={{
                background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.06)', padding: '18px 20px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  파이프라인 진행
                </div>
                {(['DXF 로드', 'DXF 파싱', 'Diff 비교', 'Notion 동기화', '완료'] as string[]).map((step, i) => {
                  const stageIdx = { idle: -1, uploading: 0, parsing: 1, diffing: 2, syncing: 3, done: 4, error: -1 }[stage] ?? -1;
                  const active = i === stageIdx;
                  const done = i < stageIdx || stage === 'done';
                  return (
                    <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: done ? 'rgba(16,185,129,0.15)' : active ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1.5px solid ${done ? '#10b981' : active ? '#6366f1' : 'rgba(255,255,255,0.08)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.3s',
                      }}>
                        {done && <I d={ico.check} c="#10b981" s={12} />}
                        {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite' }} />}
                      </div>
                      <span style={{ fontSize: 12, color: done ? '#10b981' : active ? '#e2e8f0' : '#475569', fontWeight: active ? 600 : 400 }}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Error */}
              {error && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <I d={ico.alert} c="#f87171" s={16} />
                  <div style={{ fontSize: 12, color: '#f87171', lineHeight: 1.6 }}>{error}</div>
                </div>
              )}

              {/* Parse Summary */}
              {result?.parseResult?.summary && (
                <div style={{
                  background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.06)', padding: '18px 20px', marginBottom: 16,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
 <Ruler className="w-4 h-4 inline-block" />파싱 요약 — {result.parseResult.fileName}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[
                      { label: '레이어', value: result.parseResult.summary.layerCount, color: '#818cf8' },
                      { label: '마감 레이어', value: result.parseResult.summary.finishLayerCount, color: '#10b981' },
                      { label: '폴리라인', value: result.parseResult.summary.polylineCount, color: '#60a5fa' },
                      { label: 'Room TAG', value: result.parseResult.summary.roomTagCount, color: '#f59e0b' },
                    ].map((s, i) => (
                      <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(0,0,0,0.2)' }}>
                        <div style={{ fontSize: 9, color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diff Summary */}
              {diffCounts && (
                <div style={{
                  background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.06)', padding: '18px 20px', marginBottom: 16,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
 <RefreshCw className="w-4 h-4 inline-block" />Diff 결과 {isDryRun ? '(미리보기)' : '(동기화 완료)'}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[
                      { label: '신규', value: diffCounts.create, color: '#10b981', icon: ico.plus },
                      { label: '변경', value: diffCounts.update, color: '#f59e0b', icon: ico.edit },
                      { label: '동일', value: diffCounts.unchanged, color: '#64748b', icon: ico.equal },
                      { label: '삭제', value: diffCounts.removed, color: '#ef4444', icon: ico.trash },
                    ].map((s, i) => (
                      <div key={i} style={{ padding: '10px 12px', borderRadius: 10, background: `${s.color}08`, border: `1px solid ${s.color}20`, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <I d={s.icon} c={s.color} s={16} />
                        <div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 9, color: '#475569' }}>{s.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {result?.parseResult?.warnings?.length > 0 && (
                <div style={{
                  background: 'rgba(245,158,11,0.06)', borderRadius: 12,
                  border: '1px solid rgba(245,158,11,0.12)', padding: '14px 16px', marginBottom: 16,
                }}>
 <div style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', marginBottom: 8 }}><AlertTriangle className="w-4 h-4 inline-block" />경고 ({result.parseResult.warnings.length}건)</div>
                  {result.parseResult.warnings.map((w: string, i: number) => (
                    <div key={i} style={{ fontSize: 11, color: '#fbbf24', lineHeight: 1.6, padding: '2px 0' }}>{w}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════ Results Tab ════ */}
        {tab === 'results' && result?.parseResult?.quantities && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                산출 결과 ({result.parseResult.quantities.length}건)
              </h2>
              <span style={{ fontSize: 11, color: '#64748b' }}>{result.parseResult.fileName}</span>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.02)', borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {['Room TAG', '내역코드', '분류', '수량', '단위', '레이어', '산출근거'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.parseResult.quantities.map((q: Quantity, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <td style={{ padding: '9px 14px', fontWeight: 600, color: '#e2e8f0' }}>{q.roomTag}</td>
                      <td style={{ padding: '9px 14px', color: '#818cf8', fontFamily: 'monospace', fontSize: 11 }}>{q.specCode}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: catColor(q.category) + '15', color: catColor(q.category), fontWeight: 600 }}>
                          {catLabel(q.category)}
                        </span>
                      </td>
                      <td style={{ padding: '9px 14px', fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>{q.quantity.toLocaleString()}</td>
                      <td style={{ padding: '9px 14px', color: '#64748b' }}>{q.unit}</td>
                      <td style={{ padding: '9px 14px', color: '#475569', fontFamily: 'monospace', fontSize: 10 }}>{q.layerName}</td>
                      <td style={{ padding: '9px 14px', color: '#475569', fontSize: 10, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'results' && !result?.parseResult?.quantities && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#475569' }}>
            <I d={ico.file} c="#475569" s={40} />
            <p style={{ margin: '12px 0 0', fontSize: 14 }}>파이프라인을 먼저 실행하세요</p>
          </div>
        )}

        {/* ════ CostContext Chain Tab ════ */}
        {tab === 'chain' && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>CostContext 롤업 체인</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { step: 1, title: 'DWG 원본 도면', desc: '표준 레이어/블록 적용 (FIN-FLR-*, FIN-WAL-* 등)', icon: ico.file, color: '#818cf8', link: '' },
                { step: 2, title: '자동 산출 엔진', desc: 'DXF Parser — 면적/길이/체적 추출', icon: ico.ruler, color: '#60a5fa', link: '' },
                { step: 3, title: 'RoomTag_수량산출 DB', desc: 'Notion DB — RoomTag + 내역코드 + 수량 (단일 진실 원천)', icon: ico.database, color: '#10b981', link: '' },
                { step: 4, title: '내역서_자동화', desc: 'Relation: 수량산출 DB 참조 / Rollup: 자동 수량 합산', icon: ico.layers, color: '#f59e0b', link: '' },
                { step: 5, title: '실별 집계표', desc: 'Rollup: 자재비/노무비/경비/금액 연동', icon: ico.bar, color: '#e879f9', link: '' },
                { step: 6, title: '층별 집계표', desc: 'Rollup: 실별 합계금액 → 총 공사비', icon: ico.grid, color: '#ef4444', link: '' },
              ].map((s, i) => (
                <div key={i}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px',
                    background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                    border: `1px solid ${s.color}15`,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: `${s.color}15`, border: `1px solid ${s.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <I d={s.icon} c={s.color} s={18} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 9, padding: '1px 8px', borderRadius: 6, background: `${s.color}15`, color: s.color, fontWeight: 700 }}>
                          STEP {s.step}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{s.title}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{s.desc}</div>
                    </div>
                  </div>
                  {i < 5 && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
                      <div style={{ width: 2, height: 16, background: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Diff View */}
            {result?.syncResult?.diff && result.syncResult.diff.filter((d: DiffItem) => d.action !== 'unchanged').length > 0 && (
              <div style={{
                marginTop: 24, background: 'rgba(255,255,255,0.02)', borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.06)', padding: '18px 20px',
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  최근 Diff 변경사항
                </div>
                {result.syncResult.diff.filter((d: DiffItem) => d.action !== 'unchanged').map((d: DiffItem, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <span style={{
                      fontSize: 9, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                      background: d.action === 'create' ? 'rgba(16,185,129,0.1)' : d.action === 'update' ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                      color: d.action === 'create' ? '#10b981' : d.action === 'update' ? '#f59e0b' : '#ef4444',
                    }}>
                      {d.action === 'create' ? 'NEW' : d.action === 'update' ? 'UPD' : 'DEL'}
                    </span>
                    <span style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, minWidth: 100 }}>{d.roomTag}</span>
                    <span style={{ fontSize: 11, color: '#818cf8', fontFamily: 'monospace' }}>{d.specCode}</span>
                    {d.action === 'update' && (
                      <span style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto' }}>
                        {d.oldQty} → <strong style={{ color: '#f59e0b' }}>{d.newQty}</strong>
                        <span style={{ fontSize: 10, marginLeft: 6, color: d.delta && d.delta > 0 ? '#10b981' : '#ef4444' }}>
                          ({d.deltaPercent})
                        </span>
                      </span>
                    )}
                    {d.action === 'create' && (
                      <span style={{ fontSize: 11, color: '#10b981', marginLeft: 'auto', fontWeight: 600 }}>
                        +{d.newQty}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        input::placeholder { color: #475569; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
        table th { position: sticky; top: 0; background: rgba(8,9,13,0.95); }
      `}</style>
    </div>
  );
}

// Helpers
function catColor(cat: string): string {
  const m: Record<string, string> = { floor: '#818cf8', wall: '#60a5fa', ceiling: '#f59e0b', separator: '#22d3ee', lighting_point: '#fbbf24', lighting_linear: '#fb923c', furniture: '#e879f9' };
  return m[cat] || '#94a3b8';
}
function catLabel(cat: string): string {
  const m: Record<string, string> = { floor: '바닥', wall: '벽체', ceiling: '천장', separator: '분리대', lighting_point: '조명(EA)', lighting_linear: '조명(선형)', furniture: '가구' };
  return m[cat] || cat;
}
